/**
 * EGRig - the signal chain behind the strings: pedals -> amp -> cabinet -> studio.
 *
 * WHY NATIVE NODES AND NOT A WORKLET
 * ----------------------------------
 * Distortion is the part of a rock tone everybody hears, and it is also the
 * part that aliases: a waveshaper creates harmonics above Nyquist that fold
 * back as inharmonic fizz. WaveShaperNode with oversample '4x' does the up- and
 * down-sampling in the browser's C++ with proper filters, for a fraction of
 * what a JavaScript worklet would pay on a phone. Everything else here (tone
 * stack, cabinet, delay, reverb) is a native node too, so the whole rig runs
 * off the main thread and costs the page nothing while you play.
 *
 * WHY THE DISTORTION IS AFTER THE STRINGS ARE MIXED
 * -------------------------------------------------
 * A real amp distorts the SUM of the strings. Two notes a fifth apart through
 * a nonlinearity produce a difference tone an octave below the root - that is
 * why a power chord sounds huge and why a full major chord turns to mud under
 * high gain. Sample libraries that ship a recorded "distorted note" per string
 * cannot do this; here the strings arrive as one DI signal, exactly as they do
 * at a real amp's input jack.
 *
 * WHAT EACH STAGE IS
 * ------------------
 *   pickup        resonant low-pass: the pickup's inductance against the cable
 *                 capacitance (single coil ~4 kHz, humbucker ~2.5 kHz). The
 *                 tone knob lowers it, as the tone capacitor does.
 *   pedals        compressor, wah (manual or envelope-driven from the strings),
 *                 a mid-hump boost (the metal "tightener"), and one drive pedal:
 *                 overdrive (clean + clipped mids), distortion (hard clipping,
 *                 op-amp bandwidth that closes as gain rises), big fuzz
 *                 (two clipping stages and a scooped tone), germanium fuzz.
 *   amp           cascaded triode stages (asymmetric tanh curves - even
 *                 harmonics - with coupling high-passes that decide how
 *                 "tight" the low end stays under gain), the amp's own
 *                 passive tone stack (Yeh & Smith's analytic model of the
 *                 Fender/Marshall FMV network, with real component values),
 *                 and a push-pull power stage with presence and resonance.
 *   cabinet       convolution with a speaker cabinet impulse response.
 *   studio        modulation, delay, reverb, master, safety limiter.
 *
 * The same builder runs in an OfflineAudioContext, so an export is the take
 * re-rendered through the same chain rather than a recording of the speakers.
 */
(function (root, factory) {
    var api = factory();
    if (typeof globalThis !== 'undefined') globalThis.EGRig = api;
    if (typeof window !== 'undefined') window.EGRig = api;
}(this, function () {
    'use strict';

    function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }
    function expMap(v, lo, hi) { return lo * Math.pow(hi / lo, clamp(v, 0, 1)); }
    function db(x) { return Math.pow(10, x / 20); }

    // ───────────────────────────── curves ─────────────────────────────────
    /*
     * Waveshaper curves span input [-1, 1] mapped to u in [-S, S]. Every curve
     * is normalised to peak |y| = 1; `slope` is its small-signal gain at 0, so
     * a stage can be specified by the gain it adds in dB before it saturates.
     */
    var S_RANGE = 4;
    var curveCache = {};
    function curveFn(kind, b) {
        switch (kind) {
            case 'tri':   // triode: asymmetric (grid conduction on one side) -> even harmonics
                return function (u) { return Math.tanh(u + b) - Math.tanh(b); };
            case 'pent':  // push-pull power stage: symmetric, firm knee
                return function (u) { return u / Math.pow(1 + Math.pow(Math.abs(u), 2.4), 1 / 2.4); };
            case 'diode': // silicon diodes to ground: hard, symmetric
                return function (u) { return u / Math.pow(1 + Math.pow(Math.abs(u), 4), 0.25); };
            case 'soft':  // op-amp soft clipping (diodes in the feedback loop)
                return function (u) { return Math.tanh(u); };
            case 'ge':    // germanium fuzz: one side folds early and round, the other late
                return function (u) { return u >= 0 ? Math.tanh(u * 1.3) : -Math.pow(Math.tanh(Math.pow(-u * 0.8, 1.6)), 0.8) * 0.85; };
            case 'classA': // single-ended/class-A power: strong even order, soft compression
                return function (u) { return Math.tanh(u + b) - Math.tanh(b) + 0.08 * Math.tanh(u * 0.5) * Math.tanh(u * 0.5); };
            default:
                return function (u) { return Math.tanh(u); };
        }
    }
    function curve(kind, b) {
        var key = kind + ':' + (b || 0).toFixed(3);
        if (curveCache[key]) return curveCache[key];
        var n = 8192;
        var f = curveFn(kind, b || 0);
        var c = new Float32Array(n);
        var mx = 0;
        for (var i = 0; i < n; i++) {
            var x = (i / (n - 1)) * 2 - 1;
            var y = f(x * S_RANGE);
            c[i] = y;
            var ay = Math.abs(y);
            if (ay > mx) mx = ay;
        }
        // remove the static offset at x=0 so an idle stage passes no DC
        var mid = f(0);
        for (var j = 0; j < n; j++) c[j] = (c[j] - mid) / (mx || 1);
        var eps = 1e-4;
        var slope = ((f(eps * S_RANGE) - f(-eps * S_RANGE)) / (2 * eps)) / (mx || 1);
        var out = { c: c, slope: slope };
        curveCache[key] = out;
        return out;
    }

    // ─────────────────────────── tone stacks ──────────────────────────────
    /*
     * Yeh & Smith, "Discretization of the '59 Fender Bassman Tone Stack",
     * DAFx-06: third-order analytic transfer function of the FMV network as a
     * function of the three pots. Component values are the real ones; the
     * response was checked against published tone-stack curves (tests/audio).
     */
    var K = 1e3, NF = 1e-9, PF = 1e-12;
    var STACKS = {
        bassman: { R1: 250 * K, R2: 1e6, R3: 25 * K, R4: 56 * K, C1: 250 * PF, C2: 20 * NF, C3: 20 * NF },
        marshall: { R1: 220 * K, R2: 1e6, R3: 22 * K, R4: 33 * K, C1: 470 * PF, C2: 22 * NF, C3: 22 * NF },
        blackface: { R1: 250 * K, R2: 250 * K, R3: 10 * K, R4: 100 * K, C1: 250 * PF, C2: 100 * NF, C3: 47 * NF },
        vox: { R1: 1e6, R2: 1e6, R3: 10 * K, R4: 100 * K, C1: 50 * PF, C2: 22 * NF, C3: 22 * NF },
        modern: { R1: 250 * K, R2: 1e6, R3: 25 * K, R4: 47 * K, C1: 500 * PF, C2: 22 * NF, C3: 22 * NF }
    };

    function fmvPoly(t, m, l, c) {
        var R1 = c.R1, R2 = c.R2, R3 = c.R3, R4 = c.R4, C1 = c.C1, C2 = c.C2, C3 = c.C3;
        var b1 = t * C1 * R1 + m * C3 * R3 + l * (C1 * R2 + C2 * R2) + (C1 * R3 + C2 * R3);
        var b2 = t * (C1 * C2 * R1 * R4 + C1 * C3 * R1 * R4) - m * m * (C1 * C3 * R3 * R3 + C2 * C3 * R3 * R3)
            + m * (C1 * C3 * R1 * R3 + C1 * C3 * R3 * R3 + C2 * C3 * R3 * R3)
            + l * (C1 * C2 * R1 * R2 + C1 * C2 * R2 * R4 + C1 * C3 * R2 * R4)
            + l * m * (C1 * C3 * R2 * R3 + C2 * C3 * R2 * R3)
            + (C1 * C2 * R1 * R3 + C1 * C2 * R3 * R4 + C1 * C3 * R3 * R4);
        var b3 = l * m * (C1 * C2 * C3 * R1 * R2 * R3 + C1 * C2 * C3 * R2 * R3 * R4)
            - m * m * (C1 * C2 * C3 * R1 * R3 * R3 + C1 * C2 * C3 * R3 * R3 * R4)
            + m * (C1 * C2 * C3 * R1 * R3 * R3 + C1 * C2 * C3 * R3 * R3 * R4)
            + t * C1 * C2 * C3 * R1 * R3 * R4 - t * m * C1 * C2 * C3 * R1 * R3 * R4
            + t * l * C1 * C2 * C3 * R1 * R2 * R4;
        var a1 = (C1 * R1 + C1 * R3 + C2 * R3 + C2 * R4 + C3 * R4) + m * C3 * R3 + l * (C1 * R2 + C2 * R2);
        var a2 = m * (C1 * C3 * R1 * R3 - C2 * C3 * R3 * R4 + C1 * C3 * R3 * R3 + C2 * C3 * R3 * R3)
            + l * m * (C1 * C3 * R2 * R3 + C2 * C3 * R2 * R3)
            - m * m * (C1 * C3 * R3 * R3 + C2 * C3 * R3 * R3)
            + l * (C1 * C2 * R2 * R4 + C1 * C2 * R1 * R2 + C1 * C3 * R2 * R4 + C2 * C3 * R2 * R4)
            + (C1 * C2 * R1 * R4 + C1 * C3 * R1 * R4 + C1 * C2 * R3 * R4 + C1 * C2 * R1 * R3 + C1 * C3 * R3 * R4 + C2 * C3 * R3 * R4);
        var a3 = l * m * (C1 * C2 * C3 * R1 * R2 * R3 + C1 * C2 * C3 * R2 * R3 * R4)
            - m * m * (C1 * C2 * C3 * R1 * R3 * R3 + C1 * C2 * C3 * R3 * R3 * R4)
            + m * (C1 * C2 * C3 * R3 * R3 * R4 + C1 * C2 * C3 * R1 * R3 * R3 - C1 * C2 * C3 * R1 * R3 * R4)
            + l * C1 * C2 * C3 * R1 * R2 * R4 + C1 * C2 * C3 * R1 * R3 * R4;
        return { b: [0, b1, b2, b3], a: [1, a1, a2, a3] };
    }

    /* Bilinear transform of a 3rd-order s-polynomial pair. */
    var BLT = [[1, 3, 3, 1], [1, 1, -1, -1], [1, -1, -1, 1], [1, -3, 3, -1]];
    function bilinear3(b, a, sr) {
        var Kb = 2 * sr;
        var ff = [0, 0, 0, 0], fb = [0, 0, 0, 0];
        for (var k = 0; k < 4; k++) {
            var kk = Math.pow(Kb, k);
            for (var j = 0; j < 4; j++) {
                ff[j] += b[k] * kk * BLT[k][j];
                fb[j] += a[k] * kk * BLT[k][j];
            }
        }
        var n0 = fb[0];
        for (var q = 0; q < 4; q++) { ff[q] /= n0; fb[q] /= n0; }
        return { ff: ff, fb: fb };
    }

    function stackResponseDb(type, t, m, l, f) {
        var p = fmvPoly(t, m, l, STACKS[type]);
        var w = 2 * Math.PI * f;
        var nr = 0, ni = 0, dr = 0, di = 0;
        for (var k = 0; k < 4; k++) {
            // (jw)^k
            var mag = Math.pow(w, k), ph = k % 4;
            var re = ph === 0 ? mag : (ph === 2 ? -mag : 0);
            var im = ph === 1 ? mag : (ph === 3 ? -mag : 0);
            nr += p.b[k] * re; ni += p.b[k] * im;
            dr += p.a[k] * re; di += p.a[k] * im;
        }
        return 10 * Math.log10((nr * nr + ni * ni) / (dr * dr + di * di));
    }

    /* First-order low-pass by bilinear transform: the 6 dB/octave slope of a
       real RC filter, which a biquad cannot give. */
    function onePoleLP(fc, sr) {
        var K = Math.tan(Math.PI * Math.min(fc, sr * 0.45) / sr);
        var b0 = K / (1 + K), a1 = (K - 1) / (1 + K);
        return { ff: [b0, b0], fb: [1, a1] };
    }
    function clock() { return (typeof performance !== 'undefined') ? performance.now() : Date.now(); }
    /*
     * An IIRFilterNode that can be "re-tuned". The node cannot change its
     * coefficients, so a change builds a new one on the idle branch and
     * cross-fades to it in 30 ms.
     *
     * WHAT A NODE COSTS. Chromium works out an IIRFilterNode's tail time when
     * it is created, by running ten seconds of the filter's impulse response
     * on the main thread (IIRFilter::TailTime) - 13-27 ms per node on a
     * desktop, ~100 ms at 4x CPU throttling, whatever the order. The first
     * touch on the page built ten of them (the rig was made with default
     * knobs, then cross-faded to the preset; a pedal nobody had switched on
     * was built anyway) and spent 1.9 s of a 2 s interaction on them at 4x.
     * So: a node is built only when the response really changes (`key`), the
     * first build lands on the live branch with no fade, a drag is throttled
     * by what the last build cost (the main thread stays about two thirds
     * free), and the branch that faded out is dropped instead of filtering
     * silence forever.
     */
    function SwapIIR(ctx, design) {
        this.ctx = ctx; this.design = design;
        this.arg = null; this.want = null; this.built = null;
        this.last = 0; this.gap = 40; this.pending = null; this.retire = null;
        /* A context the strings' worklet is registered on (the engine marks it)
           gets ONE eg-filter node for the life of the rig: a change is a
           message, not a build. The export (offline) and a browser without
           AudioWorklet keep the node pair below. */
        if (ctx.__egFilter && !isOffline(ctx) && typeof AudioWorkletNode !== 'undefined') {
            try { this.wk = new AudioWorkletNode(ctx, 'eg-filter', { numberOfInputs: 1, numberOfOutputs: 1 }); } catch (e) { this.wk = null; }
        }
        if (this.wk) { this.input = this.output = this.wk; return; }
        this.input = ctx.createGain(); this.output = ctx.createGain();
        this.br = [ctx.createGain(), ctx.createGain()];
        this.node = [null, null]; this.cur = 0;
        this.br[0].connect(this.output); this.br[1].connect(this.output);
        this.br[0].gain.value = 1; this.br[1].gain.value = 0;
    }
    SwapIIR.prototype._build = function (idx) {
        var z = this.design(this.arg, this.ctx.sampleRate);
        var node = this.ctx.createIIRFilter(z.ff, z.fb);
        this._drop(idx, true);
        this.input.connect(node); node.connect(this.br[idx]); this.node[idx] = node;
    };
    SwapIIR.prototype._drop = function (idx, force) {
        var n = this.node[idx];
        if (!n || (!force && idx === this.cur)) return;
        try { this.input.disconnect(n); n.disconnect(); } catch (e) {}
        this.node[idx] = null;
    };
    /* `key` names the response; without one the argument is the name. */
    SwapIIR.prototype.set = function (arg, key) {
        this.arg = arg;
        this.want = key === undefined ? (+arg).toPrecision(7) : key;
        this._apply();
    };
    SwapIIR.prototype._apply = function () {
        if (this.want === this.built) return;
        var ctx = this.ctx, self = this;
        var now = clock();
        if (this.wk) {
            // at most one new response per 40 ms: each one is a 30 ms cross-fade
            if (this.built !== null && now - this.last < 40) {
                if (!this.pending) this.pending = setTimeout(function () { self.pending = null; self._apply(); }, 45 - (now - this.last));
                return;
            }
            var z = this.design(this.arg, ctx.sampleRate);
            // the first response lands without a fade: nothing has played through yet
            this.wk.port.postMessage({ ff: z.ff, fb: z.fb, fade: this.built === null ? 0 : 0.03 });
            this.built = this.want; this.last = now;
            return;
        }
        if (!this.node[this.cur] || isOffline(ctx)) { this._build(this.cur); this.built = this.want; return; }
        if (now - this.last < this.gap) {
            if (!this.pending) this.pending = setTimeout(function () { self.pending = null; self._apply(); }, this.gap - (now - this.last) + 5);
            return;
        }
        var prev = this.cur, nxt = 1 - prev;
        this._build(nxt);
        var done = clock();
        this.gap = clamp((done - now) * 3, 40, 400);
        this.last = done;
        this.built = this.want;
        var t = ctx.currentTime;
        this.br[prev].gain.cancelScheduledValues(t); this.br[prev].gain.setValueAtTime(this.br[prev].gain.value, t); this.br[prev].gain.linearRampToValueAtTime(0, t + 0.03);
        this.br[nxt].gain.cancelScheduledValues(t); this.br[nxt].gain.setValueAtTime(0, t); this.br[nxt].gain.linearRampToValueAtTime(1, t + 0.03);
        this.cur = nxt;
        clearTimeout(this.retire);
        this.retire = setTimeout(function () { self._drop(prev); }, 80);
    };

    SwapIIR.prototype.dispose = function () {
        clearTimeout(this.pending); clearTimeout(this.retire);
        if (this.wk) { try { this.wk.port.postMessage({ type: 'dispose' }); this.wk.disconnect(); } catch (e) {} }
    };

    /* The amp's tone stack: the FMV network of the three pots, one SwapIIR. */
    function ToneStack(ctx, type) {
        var self = this;
        this.ctx = ctx;
        this.type = type;
        this.t = 0.5; this.m = 0.5; this.l = 0.5;
        this.sw = new SwapIIR(ctx, function (arg, sr) {
            var p = fmvPoly(self.t, self.m, self.l, STACKS[self.type]);
            return bilinear3(p.b, p.a, sr);
        });
        this.input = this.sw.input;
        this.output = this.sw.output;
    }
    ToneStack.prototype.set = function (t, m, l, type) {
        this.t = clamp(t, 0, 1); this.m = clamp(m, 0, 1); this.l = clamp(l, 0, 1);
        if (type && STACKS[type]) this.type = type;
        this.sw.set(null, this.type + ':' + this.t.toFixed(4) + ':' + this.m.toFixed(4) + ':' + this.l.toFixed(4));
    };

    // ───────────────────────────── FFT (for IRs) ──────────────────────────
    function fft(re, im, inverse) {
        var n = re.length, i, j, k;
        for (i = 1, j = 0; i < n; i++) {
            var bit = n >> 1;
            for (; j & bit; bit >>= 1) j ^= bit;
            j ^= bit;
            if (i < j) { var tr = re[i]; re[i] = re[j]; re[j] = tr; var ti = im[i]; im[i] = im[j]; im[j] = ti; }
        }
        for (var len = 2; len <= n; len <<= 1) {
            var ang = 2 * Math.PI / len * (inverse ? 1 : -1);
            var wr = Math.cos(ang), wi = Math.sin(ang);
            for (i = 0; i < n; i += len) {
                var cr = 1, ci = 0;
                for (k = 0; k < len / 2; k++) {
                    var ur = re[i + k], ui = im[i + k];
                    var vr = re[i + k + len / 2] * cr - im[i + k + len / 2] * ci;
                    var vi = re[i + k + len / 2] * ci + im[i + k + len / 2] * cr;
                    re[i + k] = ur + vr; im[i + k] = ui + vi;
                    re[i + k + len / 2] = ur - vr; im[i + k + len / 2] = ui - vi;
                    var ncr = cr * wr - ci * wi;
                    ci = cr * wi + ci * wr; cr = ncr;
                }
            }
        }
        if (inverse) for (i = 0; i < n; i++) { re[i] /= n; im[i] /= n; }
    }

    /* Minimum-phase impulse response from a magnitude function (dB of Hz). */
    function minPhaseIR(magDbFn, sr, n, outLen) {
        var re = new Float64Array(n), im = new Float64Array(n);
        for (var k = 0; k <= n / 2; k++) {
            var f = k * sr / n;
            var ln = Math.log(Math.max(1e-9, db(magDbFn(f))));
            re[k] = ln; im[k] = 0;
            if (k > 0 && k < n / 2) { re[n - k] = ln; im[n - k] = 0; }
        }
        fft(re, im, true);                      // real cepstrum
        for (var i = 1; i < n / 2; i++) { re[i] *= 2; im[i] *= 2; }
        for (var q = n / 2 + 1; q < n; q++) { re[q] = 0; im[q] = 0; }
        fft(re, im, false);
        for (var z = 0; z < n; z++) {           // exp of complex spectrum
            var e = Math.exp(re[z]);
            var c = Math.cos(im[z]), s = Math.sin(im[z]);
            re[z] = e * c; im[z] = e * s;
        }
        fft(re, im, true);
        var h = new Float32Array(outLen);
        var fade = Math.floor(outLen * 0.25);
        for (var t = 0; t < outLen; t++) {
            var w = t < outLen - fade ? 1 : 0.5 * (1 + Math.cos(Math.PI * (t - (outLen - fade)) / fade));
            h[t] = re[t] * w;
        }
        return h;
    }

    // ─────────────────────────────── cabinets ─────────────────────────────
    /*
     * Magnitude responses of the cabinet archetypes (on-axis dynamic mic,
     * cone near-centre), built from the published frequency charts of the
     * speakers they are named after and turned into minimum-phase responses.
     * Real measured IRs, when shipped, replace these by key (see IR_URLS).
     */
    function peak(f, fc, g, q) {
        var x = Math.log2(f / fc) * q * 1.2;
        return g / (1 + x * x);
    }
    function hpRes(f, fc, q) {              // 2nd-order resonant high-pass, dB
        var r = f / fc;
        var re = 1 - 1 / (r * r), im = 1 / (q * r);
        return -10 * Math.log10(re * re + im * im);
    }
    function lpSlope(f, fc, orderDbPerOct) {
        return f <= fc ? 0 : -orderDbPerOct * Math.log2(f / fc) * Math.min(1, Math.log2(f / fc) * 1.6 + 0.3);
    }
    function ripple(seed) {
        // small cabinet-reflection ripple, a few dB, seeded so it never changes
        var parts = [];
        var a = seed;
        for (var i = 0; i < 9; i++) {
            a = (a * 16807) % 2147483647;
            var fc = 250 * Math.pow(2, (a % 1000) / 1000 * 4.2);
            a = (a * 16807) % 2147483647;
            var g = ((a % 1000) / 1000 - 0.5) * 3.2;
            parts.push([fc, g]);
        }
        return function (f) {
            var s = 0;
            for (var i = 0; i < parts.length; i++) s += peak(f, parts[i][0], parts[i][1], 5);
            return s;
        };
    }
    var CABS = {
        v30: {              // closed 4x12, V30-type: tight low end, 2-4 kHz bite, steep top roll-off
            fn: (function () { var r = ripple(12); return function (f) {
                return hpRes(f, 88, 1.35) + peak(f, 130, 2.5, 1.6) + peak(f, 1400, -2.2, 1.4)
                    + peak(f, 2600, 4.5, 1.3) + peak(f, 4300, 3.5, 2.2) + lpSlope(f, 5200, 30) + r(f); }; })(),
            gain: 0.9
        },
        greenback: {        // closed 4x12, Greenback-type: smoother, earlier roll-off, warm
            fn: (function () { var r = ripple(33); return function (f) {
                return hpRes(f, 80, 1.25) + peak(f, 120, 2, 1.5) + peak(f, 900, 1, 1.0)
                    + peak(f, 2100, 3.5, 1.2) + peak(f, 3600, 1.5, 2) + lpSlope(f, 4700, 30) + r(f); }; })(),
            gain: 0.95
        },
        '2x12o': {          // open-back 2x12, Alnico-type: chime, less thump
            fn: (function () { var r = ripple(71); return function (f) {
                return hpRes(f, 105, 0.8) + peak(f, 700, -1.5, 1.2) + peak(f, 2800, 4, 1.4)
                    + peak(f, 5000, 3, 2) + lpSlope(f, 6200, 26) + r(f); }; })(),
            gain: 1.0
        },
        '1x12o': {          // open-back 1x12, US ceramic-type: bright, scooped low mids
            fn: (function () { var r = ripple(97); return function (f) {
                return hpRes(f, 115, 0.75) + peak(f, 400, -2, 1.0) + peak(f, 2300, 2.5, 1.2)
                    + peak(f, 4500, 3, 1.8) + lpSlope(f, 6800, 24) + r(f); }; })(),
            gain: 1.05
        },
        g12f: {             // closed 4x12, big low-mid speaker (doom, stoner)
            fn: (function () { var r = ripple(44); return function (f) {
                return hpRes(f, 78, 1.3) + peak(f, 150, 6, 1.4) + peak(f, 500, 3, 1.2)
                    + peak(f, 2200, 4, 1.3) + lpSlope(f, 4800, 30) + r(f); }; })(),
            gain: 0.9
        },
        dv77: {             // closed 4x12 with an aggressive modern speaker: tight, mid-forward
            fn: (function () { var r = ripple(55); return function (f) {
                return hpRes(f, 95, 1.2) + peak(f, 150, 1.5, 1.4) + peak(f, 800, 1.5, 1.0)
                    + peak(f, 2400, 3.5, 1.3) + peak(f, 4000, 2, 2) + lpSlope(f, 5600, 28) + r(f); }; })(),
            gain: 0.95
        }
    };
    var irCache = {};
    var revOrder = [];      // reverb impulse keys, oldest first
    /*
     * Measured cabinets (CC0 captures, see public/data/electric-guitar/CREDITS.md)
     * are registered here once decoded; until then - or if the file never
     * arrives - the synthetic response of the same archetype plays, so the
     * rig is never silent and never waits on the network.
     */
    var cabData = {};
    function registerCab(key, data, sr) {
        cabData[key] = { data: data, sr: sr };
        for (var k in irCache) if (k.indexOf(key + '@') === 0) delete irCache[k];
    }
    function resample(data, from, to) {
        if (from === to) return data;
        var n = Math.round(data.length * to / from), out = new Float32Array(n);
        var ratio = from / to, half = 8, cut = Math.min(1, to / from);
        for (var i = 0; i < n; i++) {
            var x = i * ratio, c = Math.floor(x), acc = 0;
            for (var j = c - half + 1; j <= c + half; j++) {
                if (j < 0 || j >= data.length) continue;
                var t = x - j, sinc = t === 0 ? 1 : Math.sin(Math.PI * t * cut) / (Math.PI * t * cut);
                var w = 0.5 * (1 + Math.cos(Math.PI * t / half));
                acc += data[j] * sinc * cut * w;
            }
            out[i] = acc;
        }
        return out;
    }
    function cabBuffer(ctx, key, fresh) {
        var ck = key + '@' + ctx.sampleRate;
        if (!fresh && irCache[ck] && irCache[ck].sampleRate === ctx.sampleRate) return irCache[ck];
        var real = cabData[key];
        if (real) {
            var d = resample(real.data, real.sr, ctx.sampleRate);
            var rb = ctx.createBuffer(1, d.length, ctx.sampleRate);
            rb.getChannelData(0).set(d);
            // equal loudness with the synthetic set: normalise energy
            var en = 0; var ch = rb.getChannelData(0);
            for (var q = 0; q < ch.length; q++) en += ch[q] * ch[q];
            var gg = 0.35 / Math.sqrt(en || 1);
            for (var z = 0; z < ch.length; z++) ch[z] *= gg;
            irCache[ck] = rb;
            return rb;
        }
        var cab = CABS[key] || CABS.v30;
        var sr = ctx.sampleRate;
        var h = minPhaseIR(cab.fn, sr, 16384, Math.round(sr * 0.045));
        // loudness-normalise: equal energy through the voice band
        var e = 0;
        for (var i = 0; i < h.length; i++) e += h[i] * h[i];
        var g = cab.gain / Math.sqrt(e || 1) * 0.35;
        for (var j = 0; j < h.length; j++) h[j] *= g;
        var buf = ctx.createBuffer(1, h.length, sr);
        buf.copyToChannel ? buf.copyToChannel(h, 0) : buf.getChannelData(0).set(h);
        irCache[ck] = buf;
        return buf;
    }

    // ─────────────────────────────── reverbs ──────────────────────────────
    function noiseRng(seed) {
        var a = seed | 0;
        return function () {
            a = (a + 0x6D2B79F5) | 0;
            var t = Math.imul(a ^ (a >>> 15), 1 | a);
            t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
            return (((t ^ (t >>> 14)) >>> 0) / 4294967296) * 2 - 1;
        };
    }
    /*
     * Stereo reverb IR: early reflections + a noise tail whose high frequencies
     * die faster than the lows (air and wall absorption), decorrelated L/R.
     */
    function reverbBuffer(ctx, type, decay, fresh) {
        var sr = ctx.sampleRate;
        var P = {
            room: { t60: 0.7, pre: 0.004, er: 7, lp0: 9000, lp1: 2500, width: 0.8 },
            plate: { t60: 1.8, pre: 0.0, er: 0, lp0: 12000, lp1: 5000, width: 1.0 },
            hall: { t60: 3.0, pre: 0.022, er: 10, lp0: 8000, lp1: 2200, width: 1.0 },
            huge: { t60: 7.0, pre: 0.03, er: 0, lp0: 7000, lp1: 1800, width: 1.0 },
            reverse: { t60: 2.2, pre: 0, er: 0, lp0: 9000, lp1: 3000, width: 1.0 }
        }[type] || { t60: 1.5, pre: 0.01, er: 4, lp0: 9000, lp1: 3000, width: 1.0 };
        var t60 = P.t60 * (0.5 + decay);
        var ck = type + ':' + t60.toFixed(2) + '@' + sr;
        if (!fresh && irCache[ck] && irCache[ck].sampleRate === sr) return irCache[ck];
        var len = Math.min(Math.round(sr * (t60 * 1.1 + P.pre)), sr * 9);
        var buf = ctx.createBuffer(2, len, sr);
        for (var ch = 0; ch < 2; ch++) {
            var d = buf.getChannelData(ch);
            var rnd = noiseRng(ch === 0 ? 1111 : 2222);
            var lpState = 0;
            var pre = Math.round(P.pre * sr);
            /* three exp() per sample made a hall IR (8 s x 2 channels) cost
               ~260 ms of the first touch at 4x CPU; the envelope is a constant
               ratio per sample and the cutoff moves over tenths of a second,
               so it is recomputed every 32 samples (0.7 ms) */
            var envStep = Math.exp(-6.9078 / (sr * t60)), env = 1, k = 0;
            for (var i = pre; i < len; i++) {
                var t = (i - pre) / sr;
                if (((i - pre) & 31) === 0) {
                    // low-pass cutoff falls over the tail
                    var fc = P.lp1 + (P.lp0 - P.lp1) * Math.exp(-t / (t60 * 0.35));
                    k = 1 - Math.exp(-2 * Math.PI * fc / sr);
                }
                lpState += (rnd() - lpState) * k;
                var build = t < 0.012 ? t / 0.012 : 1;
                d[i] = lpState * env * build;
                env *= envStep;
            }
            for (var e = 0; e < P.er; e++) {
                var tap = Math.round((0.006 + 0.004 * e + 0.003 * (rnd() + 1)) * sr) + pre;
                if (tap < len) d[tap] += (0.6 - e * 0.05) * (rnd() > 0 ? 1 : -1);
            }
        }
        if (type === 'reverse') {
            for (var c2 = 0; c2 < 2; c2++) {
                var dd = buf.getChannelData(c2);
                Array.prototype.reverse.call(dd);
            }
        }
        // normalise energy
        var en = 0;
        for (var c3 = 0; c3 < 2; c3++) { var x = buf.getChannelData(c3); for (var q = 0; q < x.length; q++) en += x[q] * x[q]; }
        var g = 0.5 / Math.sqrt(en || 1);
        for (var c4 = 0; c4 < 2; c4++) { var y = buf.getChannelData(c4); for (var r = 0; r < y.length; r++) y[r] *= g; }
        irCache[ck] = buf;
        // the last few only: every decay step used to stay in memory for the page's life
        revOrder.push(ck);
        while (revOrder.length > 6) delete irCache[revOrder.shift()];
        return buf;
    }

    // ─────────────────────────────── helpers ──────────────────────────────
    function isOffline(ctx) { return typeof OfflineAudioContext !== 'undefined' && ctx instanceof OfflineAudioContext; }
    function setP(param, v, ctx, tc) {
        if (!param) return;
        if (isOffline(ctx) || ctx.state !== 'running') { param.value = v; return; }
        var t = ctx.currentTime;
        try { param.cancelScheduledValues(t); param.setTargetAtTime(v, t, tc || 0.012); } catch (e) { param.value = v; }
    }
    function biq(ctx, type, f, q, g) {
        var b = ctx.createBiquadFilter();
        b.type = type; b.frequency.value = f;
        if (q !== undefined) b.Q.value = q;
        if (g !== undefined) b.gain.value = g;
        return b;
    }
    function shaper(ctx, kind, bias, os) {
        var w = ctx.createWaveShaper();
        var c = curve(kind, bias);
        w.curve = c.c;
        w.oversample = os || '4x';
        w._slope = c.slope;
        return w;
    }
    /*
     * An AnalyserNode for a meter or a tuner, or null. Its size is a wish, not
     * a condition of playing: an anti-fingerprinting extension (Yandex Browser
     * and Chromium builds on Android, 17 sessions in October 2026, then the
     * same visitor on /online-piano) redefines AnalyserNode.prototype with
     * getter-only properties, and in strict code `an.fftSize = 2048` THROWS -
     * the guitar's start failed on a level meter nobody needs to play
     * ("Cannot set property fftSize of #<AnalyserNode> which has only a
     * getter", eg_start_failed). Readers size their buffer from the node
     * (an.fftSize), and read through readLevel(), which a tampered getter
     * cannot throw out of.
     */
    function analyser(ctx, size) {
        var an;
        try { an = ctx.createAnalyser(); } catch (e) { return null; }
        try { if (an.fftSize !== size) an.fftSize = size; } catch (e) {}
        return an;
    }
    function readLevel(an, keep) {
        if (!an) return null;
        try {
            var n = an.fftSize | 0;
            if (!(n > 0)) return null;
            var buf = keep && keep.length === n ? keep : new Float32Array(n);
            an.getFloatTimeDomainData(buf);
            var pk = 0, s = 0;
            for (var i = 0; i < n; i++) { var x = buf[i]; s += x * x; if (x > pk) pk = x; else if (-x > pk) pk = -x; }
            return { peak: pk, rms: Math.sqrt(s / n), buf: buf };
        } catch (e) { return null; }
    }
    /*
     * A convolver takes only a buffer of its own context's rate. The caches
     * above are keyed by rate, and still one Chrome on Android (30.09.2026)
     * threw "The buffer sample rate of 48000 does not match the context rate
     * of 44100 Hz" while the rig was being built - and the guitar did not
     * start at all. A buffer the node refuses is made again for this context;
     * if it is refused again the caller plays on without that convolver.
     */
    function assignConv(conv, buf, rebuild) {
        try { conv.buffer = buf; return true; } catch (e) {}
        try { conv.buffer = rebuild(); return true; } catch (e2) { return false; }
    }
    /* Chain nodes in order; returns [first, last]. */
    function chain(list) {
        for (var i = 0; i < list.length - 1; i++) list[i].connect(list[i + 1]);
        return [list[0], list[list.length - 1]];
    }
    /* A pedal/fx wrapper with click-free bypass: dry and wet gains. */
    function Bypass(ctx) {
        this.ctx = ctx;
        this.input = ctx.createGain();
        this.output = ctx.createGain();
        this.dry = ctx.createGain();
        this.wet = ctx.createGain();
        this.input.connect(this.dry);
        this.dry.connect(this.output);
        this.wet.connect(this.output);
        this.on = false;
        this.dry.gain.value = 1;
        this.wet.gain.value = 0;
        this.sendIn = null;          // node the wet path starts at
        this.connected = false;
        this.mixDry = 1;             // for time effects: dry stays at 1 when on
    }
    Bypass.prototype.attach = function (first, last) {
        this.sendIn = first;
        last.connect(this.wet);
    };
    Bypass.prototype.set = function (on, keepDry) {
        var ctx = this.ctx;
        var self = this;
        this.on = !!on;
        if (on && !this.connected && this.sendIn) { this.input.connect(this.sendIn); this.connected = true; }
        setP(this.wet.gain, on ? 1 : 0, ctx, 0.008);
        setP(this.dry.gain, on ? (keepDry ? 1 : 0) : 1, ctx, 0.008);
        if (!on && this.connected) {
            // disconnect after the fade so a bypassed convolver costs nothing
            if (isOffline(ctx)) { try { this.input.disconnect(this.sendIn); } catch (e) {} this.connected = false; return; }
            clearTimeout(this._t);
            this._t = setTimeout(function () {
                if (!self.on && self.connected) { try { self.input.disconnect(self.sendIn); } catch (e) {} self.connected = false; }
            }, 90);
        }
    };

    // ───────────────────────────── amp models ─────────────────────────────
    /*
     * Each amp is a list of stages. `g` is the stage's small-signal gain in
     * dB at the knob's minimum and maximum; `k` names the knob that drives it.
     * hp = coupling capacitor high-pass (tightness), lp = plate/Miller roll-off,
     * sh = cathode/bright shelf before the stage.
     */
    /* `level` evens the models out: measured on one riff at gain 0.5 the clean
       model came out 20 dB below the high-gain one (-34.5 vs -12.4 dB RMS),
       so switching genre was a volume jump before it was a tone change. */
    var AMPS = {
        clean: {
            stages: [
                { hp: 30, lp: 12000, kind: 'tri', b: 0.08, g: [-10, 12], k: 'gain', bright: [3200, 3] },
                { stack: 'blackface' },
                { hp: 25, lp: 11000, kind: 'tri', b: 0.1, g: [4, 4] }
            ],
            power: { hp: 60, lp: 9500, kind: 'pent', g: [-12, 8], presF: 4200, resF: 100 },
            level: 6.3
        },
        tweed: {
            stages: [
                { hp: 25, lp: 7800, kind: 'tri', b: 0.15, g: [-6, 20], k: 'gain' },
                { hp: 40, lp: 7000, kind: 'tri', b: 0.2, g: [6, 10], k: 'gain' },
                { stack: 'bassman' }
            ],
            power: { hp: 55, lp: 7000, kind: 'pent', g: [-10, 10], presF: 3800, resF: 95 },
            level: 1.26
        },
        chime: {
            stages: [
                { hp: 60, lp: 10500, kind: 'tri', b: 0.12, g: [-4, 20], k: 'gain', bright: [2600, 4] },
                { stack: 'vox' },
                { hp: 70, lp: 9000, kind: 'tri', b: 0.18, g: [6, 12], k: 'gain' }
            ],
            power: { hp: 80, lp: 8800, kind: 'classA', b: 0.3, g: [-8, 12], presF: 4500, resF: 110 },
            level: 0.9
        },
        plexi: {
            stages: [
                { hp: 25, lp: 9000, kind: 'tri', b: 0.15, g: [-2, 24], k: 'gain', bright: [3000, 3] },
                { hp: 70, lp: 8000, kind: 'tri', b: 0.22, g: [6, 14], k: 'gain' },
                { stack: 'marshall' }
            ],
            power: { hp: 70, lp: 7600, kind: 'pent', g: [-10, 10], presF: 3600, resF: 95 },
            level: 0.8
        },
        brit800: {
            stages: [
                { hp: 20, lp: 10000, kind: 'tri', b: 0.15, g: [0, 26], k: 'gain', bright: [2200, 4] },
                { hp: 150, lp: 7200, kind: 'tri', b: 0.34, g: [12, 20], k: 'gain', shelf: [420, -4] },
                { hp: 60, lp: 7000, kind: 'soft', b: 0, g: [0, 0] },
                { stack: 'marshall' }
            ],
            power: { hp: 80, lp: 7200, kind: 'pent', g: [-10, 10], presF: 3600, resF: 95 },
            level: 0.75
        },
        modern: {
            stages: [
                { hp: 85, lp: 9500, kind: 'tri', b: 0.15, g: [8, 30], k: 'gain', shelf: [320, -6] },
                { hp: 190, lp: 6800, kind: 'tri', b: 0.25, g: [14, 22], k: 'gain' },
                { hp: 110, lp: 6200, kind: 'tri', b: 0.3, g: [8, 12], k: 'gain' },
                { stack: 'modern' }
            ],
            power: { hp: 90, lp: 7800, kind: 'pent', g: [-10, 10], presF: 3300, resF: 90 },
            level: 0.67
        },
        bass: {
            stages: [
                { hp: 35, lp: 5000, kind: 'tri', b: 0.1, g: [-6, 12], k: 'gain' },
                { stack: 'bassman' }
            ],
            power: { hp: 38, lp: 4500, kind: 'pent', g: [-10, 6], presF: 2500, resF: 70 },
            level: 1.0
        }
    };

    function Amp(ctx, model, p) {
        this.ctx = ctx;
        this.input = ctx.createGain();
        this.output = ctx.createGain();
        this.model = null;
        this.nodes = [];
        this.gainStages = [];
        this.stack = null;
        this.p = { gain: 0.5, bass: 0.5, mid: 0.5, treble: 0.5, presence: 0.5, master: 0.5, res: 0.3 };
        if (p) for (var k in p) if (Object.prototype.hasOwnProperty.call(p, k) && k !== 'model') this.p[k] = p[k];
        this.build(model || 'plexi');
    }
    Amp.prototype.build = function (model) {
        var ctx = this.ctx;
        var spec = AMPS[model] || AMPS.plexi;
        // tear down
        try { this.input.disconnect(); } catch (e) {}
        if (this.stack) this.stack.sw.dispose();
        for (var i = 0; i < this.nodes.length; i++) { try { this.nodes[i].disconnect(); } catch (e) {} }
        this.nodes = [];
        this.gainStages = [];
        this.stack = null;
        this.model = model;
        if (model === 'none') {           // measurement / "DI into the pedals" path
            this.input.connect(this.output);
            this.power = null; this.pres = null; this.reso = null;
            return;
        }

        var prev = this.input;
        var self = this;
        function add(n) { prev.connect(n); self.nodes.push(n); prev = n; return n; }
        for (var s = 0; s < spec.stages.length; s++) {
            var st = spec.stages[s];
            if (st.stack) {
                this.stack = new ToneStack(ctx, st.stack);
                prev.connect(this.stack.input);
                this.nodes.push(this.stack.input, this.stack.output);
                prev = this.stack.output;
                // the passive stack loses ~10-20 dB; the next stage makes it back up
                var mk = ctx.createGain(); mk.gain.value = db(6);
                add(mk);
                continue;
            }
            if (st.hp) add(biq(ctx, 'highpass', st.hp, 0.6));
            if (st.shelf) add(biq(ctx, 'lowshelf', st.shelf[0], 0.7, st.shelf[1]));
            var bright = null;
            if (st.bright) bright = add(biq(ctx, 'highshelf', st.bright[0], 0.7, st.bright[1]));
            var pre = add(ctx.createGain());
            var sh = add(shaper(ctx, st.kind, st.b || 0, '4x'));
            add(biq(ctx, 'highpass', 8, 0.5));          // DC from the asymmetric curve
            add(biq(ctx, 'lowpass', st.lp, 0.6));
            this.gainStages.push({ pre: pre, sh: sh, spec: st, bright: bright });
        }
        // power amp
        var pw = spec.power;
        var ppre = add(ctx.createGain());
        var psh = add(shaper(ctx, pw.kind, pw.b || 0, '2x'));
        add(biq(ctx, 'highpass', pw.hp, 0.7));
        this.pres = add(biq(ctx, 'highshelf', pw.presF, 0.7, 0));
        this.reso = add(biq(ctx, 'peaking', pw.resF, 1.1, 0));
        add(biq(ctx, 'lowpass', pw.lp, 0.7));
        var lvl = add(ctx.createGain());
        lvl.gain.value = spec.level;
        prev.connect(this.output);
        this.power = { pre: ppre, sh: psh, spec: pw };
        this.apply();
    };
    Amp.prototype.set = function (p) {
        for (var k in p) if (Object.prototype.hasOwnProperty.call(p, k)) this.p[k] = p[k];
        this.apply();
    };
    Amp.prototype.apply = function () {
        var ctx = this.ctx, p = this.p;
        for (var i = 0; i < this.gainStages.length; i++) {
            var g = this.gainStages[i];
            var gdB = g.spec.k ? g.spec.g[0] + (g.spec.g[1] - g.spec.g[0]) * p.gain : g.spec.g[0];
            setP(g.pre.gain, db(gdB) / g.sh._slope, ctx);
            // the bright cap matters at low gain and disappears as the pot opens
            if (g.bright) setP(g.bright.gain, g.spec.bright[1] * (1 - 0.8 * p.gain), ctx);
        }
        if (this.stack) this.stack.set(p.treble, p.mid, p.bass);
        var pw = this.power;
        if (!pw) return;
        var pdB = pw.spec.g[0] + (pw.spec.g[1] - pw.spec.g[0]) * p.master;
        setP(pw.pre.gain, db(pdB) / pw.sh._slope, ctx);
        setP(this.pres.gain, (p.presence - 0.3) * 12, ctx);
        setP(this.reso.gain, p.res * 7, ctx);
    };

    // ─────────────────────────────── pedals ───────────────────────────────
    function Drive(ctx) {
        this.ctx = ctx;
        this.bp = new Bypass(ctx);
        this.input = this.bp.input;
        this.output = this.bp.output;
        this.type = null;
        this.nodes = [];
        this.n = null;
        this.p = { type: 'od', drive: 0.5, tone: 0.5, level: 0.5 };
        /* nothing is built until the pedal is switched on: the bass rig's
           drive never is, and the guitar's may not be for a whole session */
    }
    Drive.prototype.build = function (type) {
        var ctx = this.ctx;
        for (var i = 0; i < this.nodes.length; i++) { try { this.nodes[i].disconnect(); } catch (e) {} }
        if (this.n) ['rc', 'tone'].forEach(function (k) { var f = this.n[k]; if (f && f.dispose) f.dispose(); }, this);
        this.nodes = [];
        if (this.bp.connected) { try { this.bp.input.disconnect(this.bp.sendIn); } catch (e) {} this.bp.connected = false; }
        this.type = type;
        var n = {};
        var inG = ctx.createGain();
        var out = ctx.createGain();
        var keep = [inG, out];
        if (type === 'od') {
            /* Overdrive = the Tube Screamer's topology (ElectroSmash analysis):
               the op-amp amplifies only above ~720 Hz (4.7k + 47 nF in its
               ground leg), soft diodes clip that part, and the dry signal
               rides through at unity - so the lows stay clean and the mids
               break up: the famous hump. Then a fixed 723 Hz RC low-pass
               (1k / 0.22 uF) and the tone control as a treble shelf.
               Calibrated on EGFxSet: our first version kept 12 dB too little
               at 200 Hz and 8 dB too much at 2.5 kHz against the real pedal. */
            n.clean = ctx.createGain();
            n.hp = biq(ctx, 'highpass', 720, 0.5);
            n.pre = ctx.createGain();
            n.sh = shaper(ctx, 'soft', 0, '4x');
            n.clipLvl = ctx.createGain();
            n.lp = biq(ctx, 'lowpass', 6000, 0.6);
            n.sum = ctx.createGain();
            n.rc = new SwapIIR(ctx, onePoleLP);
            n.rc.set(723);
            n.rcDry = ctx.createGain();       // the tone network is a blend, not a pure low-pass
            n.rcSum = ctx.createGain();
            n.tone = biq(ctx, 'highshelf', 1600, 0.6, 0);
            n.lvl = ctx.createGain();
            inG.connect(n.clean); n.clean.connect(n.sum);
            chain([inG, n.hp, n.pre, n.sh, n.clipLvl, n.lp, n.sum]);
            n.sum.connect(n.rc.input); n.rc.output.connect(n.rcSum);
            n.sum.connect(n.rcDry); n.rcDry.connect(n.rcSum);
            chain([n.rcSum, n.tone, n.lvl, out]);
            keep.push(n.clean, n.hp, n.pre, n.sh, n.clipLvl, n.lp, n.sum, n.rc.input, n.rc.output, n.rcDry, n.rcSum, n.tone, n.lvl);
        } else if (type === 'ds') {
            /* Distortion = the RAT: op-amp gain rising with frequency (two RC
               legs, 60 Hz and 1.5 kHz), an LM308 whose bandwidth closes as
               the gain goes up, 1N914s to ground (hard, symmetric), then the
               FILTER - a first-order RC low-pass from ~32 kHz down to ~475 Hz,
               ~0.9 kHz at noon. A biquad (12 dB/oct) made it 4-5 dB too bright
               at 1.6-2.5 kHz and too dark at 7-10 kHz against the real RAT2
               (EGFxSet); the 6 dB/oct slope is the pedal. */
            n.hp = biq(ctx, 'highpass', 60, 0.6);
            n.tilt = biq(ctx, 'highshelf', 1200, 0.6, 8);
            n.pre = ctx.createGain();
            n.gbw = biq(ctx, 'lowpass', 8000, 0.6);
            n.sh = shaper(ctx, 'diode', 0, '4x');
            n.tone = new SwapIIR(ctx, onePoleLP);
            n.lvl = ctx.createGain();
            chain([inG, n.hp, n.tilt, n.pre, n.gbw, n.sh, n.tone.input]);
            chain([n.tone.output, n.lvl, out]);
            keep.push(n.hp, n.tilt, n.pre, n.gbw, n.sh, n.tone.input, n.tone.output, n.lvl);
        } else if (type === 'fz') {
            // big fuzz: two clipping stages, then the scooped tone control
            n.hp = biq(ctx, 'highpass', 90, 0.6);
            n.pre = ctx.createGain();
            n.sh1 = shaper(ctx, 'soft', 0, '4x');
            n.lp1 = biq(ctx, 'lowpass', 4200, 0.6);
            n.pre2 = ctx.createGain();
            n.sh2 = shaper(ctx, 'soft', 0, '4x');
            n.lp2 = biq(ctx, 'lowpass', 3800, 0.6);
            n.lo = biq(ctx, 'lowpass', 480, 0.7);
            n.hi = biq(ctx, 'highpass', 1100, 0.7);
            n.loG = ctx.createGain(); n.hiG = ctx.createGain();
            n.lvl = ctx.createGain();
            chain([inG, n.hp, n.pre, n.sh1, n.lp1, n.pre2, n.sh2, n.lp2]);
            n.lp2.connect(n.lo); n.lp2.connect(n.hi);
            n.lo.connect(n.loG); n.hi.connect(n.hiG);
            n.loG.connect(n.lvl); n.hiG.connect(n.lvl);
            n.lvl.connect(out);
            n.pre2.gain.value = 4;
            keep.push(n.hp, n.pre, n.sh1, n.lp1, n.pre2, n.sh2, n.lp2, n.lo, n.hi, n.loG, n.hiG, n.lvl);
        } else if (type === 'ge') {
            // germanium fuzz: asymmetric, round, cleans up when the guitar is turned down
            n.hp = biq(ctx, 'highpass', 50, 0.6);
            n.pre = ctx.createGain();
            n.sh = shaper(ctx, 'ge', 0, '4x');
            n.lp = biq(ctx, 'lowpass', 5200, 0.6);
            n.tone = biq(ctx, 'lowpass', 7000, 0.5);
            n.lvl = ctx.createGain();
            chain([inG, n.hp, n.pre, n.sh, n.lp, n.tone, n.lvl, out]);
            keep.push(n.hp, n.pre, n.sh, n.lp, n.tone, n.lvl);
        }
        this.n = n;
        this.nodes = keep;
        this.bp.attach(inG, out);
        if (this.bp.on) this.bp.set(true);
        this.apply();
    };
    Drive.prototype.set = function (p) {
        for (var k in p) if (k !== 'on' && (k !== 'type' || p.type)) this.p[k] = p[k];
        var on = p.on !== undefined ? !!p.on : this.bp.on;
        // built when first heard, rebuilt when the type changes while heard
        if (on && this.p.type !== this.type) this.build(this.p.type);
        if (p.on !== undefined) this.bp.set(p.on);
        this.apply();
    };
    Drive.prototype.apply = function () {
        var ctx = this.ctx, n = this.n, p = this.p;
        if (!n) return;
        if (this.type === 'od') {
            // gain of the clipping stage: 1 + (51k + 500k*drive) / 4.7k = 12..118
            var tsg = 1 + (51 + 500 * p.drive) / 4.7;
            setP(n.pre.gain, tsg / n.sh._slope, ctx);
            setP(n.clipLvl.gain, 0.22, ctx);                       // diode knee vs the dry path
            setP(n.lp.frequency, clamp(1 / (2 * Math.PI * (51e3 + 500e3 * p.drive) * 51e-12), 5000, 20000), ctx);
            setP(n.clean.gain, 1.0, ctx);
            setP(n.rcDry.gain, 0.12, ctx);
            setP(n.tone.gain, (p.tone - 0.5) * 20, ctx);
            setP(n.lvl.gain, expMap(p.level, 0.25, 4), ctx);
        } else if (this.type === 'ds') {
            var g = expMap(p.drive, 4, 900);
            setP(n.pre.gain, g / n.sh._slope, ctx);
            setP(n.gbw.frequency, clamp(6e6 / g, 6500, 18000), ctx);
            // FILTER pot 0..100k in series with 1.5k, 3.3 nF to ground; the
            // knob is reverse (clockwise = darker), our `tone` is "brightness"
            n.tone.set(1.45 / (2 * Math.PI * (1.5e3 + 100e3 * (1 - p.tone)) * 3.3e-9));
            setP(n.lvl.gain, expMap(p.level, 0.08, 1.2), ctx);
        } else if (this.type === 'fz') {
            setP(n.pre.gain, expMap(p.drive, 3, 80) / n.sh1._slope, ctx);
            setP(n.loG.gain, (1 - p.tone) * 1.6, ctx);
            setP(n.hiG.gain, p.tone * 1.6, ctx);
            setP(n.lvl.gain, expMap(p.level, 0.1, 1.4), ctx);
        } else if (this.type === 'ge') {
            setP(n.pre.gain, expMap(p.drive, 2, 40) / n.sh._slope, ctx);
            setP(n.tone.frequency, expMap(p.tone, 1800, 9000), ctx);
            setP(n.lvl.gain, expMap(p.level, 0.1, 1.4), ctx);
        }
    };

    /* Mid-hump boost: the Tube-Screamer-as-tightener trick in front of a
       high-gain amp. Cuts lows before the amp clips them into mud. */
    function Boost(ctx) {
        this.ctx = ctx;
        this.bp = new Bypass(ctx);
        this.input = this.bp.input; this.output = this.bp.output;
        var ls = biq(ctx, 'lowshelf', 420, 0.7, -9);
        var pk = biq(ctx, 'peaking', 850, 0.8, 4);
        var hs = biq(ctx, 'highshelf', 5200, 0.7, -3);
        this.pre = ctx.createGain();
        this.sh = shaper(ctx, 'soft', 0, '2x');
        this.lvl = ctx.createGain();
        var c = chain([ls, pk, hs, this.pre, this.sh, this.lvl]);
        this.bp.attach(c[0], c[1]);
        this.p = { drive: 0.15, level: 0.7 };
        this.apply();
    }
    Boost.prototype.set = function (p) {
        for (var k in p) if (k !== 'on') this.p[k] = p[k];
        if (p.on !== undefined) this.bp.set(p.on);
        this.apply();
    };
    Boost.prototype.apply = function () {
        setP(this.pre.gain, expMap(this.p.drive, 0.5, 6) / this.sh._slope, this.ctx);
        setP(this.lvl.gain, expMap(this.p.level, 0.5, 4), this.ctx);
    };

    function Comp(ctx) {
        this.ctx = ctx;
        this.bp = new Bypass(ctx);
        this.input = this.bp.input; this.output = this.bp.output;
        this.c = ctx.createDynamicsCompressor();
        this.c.knee.value = 6; this.c.ratio.value = 6; this.c.attack.value = 0.006; this.c.release.value = 0.22;
        this.mk = ctx.createGain();
        this.c.connect(this.mk);
        this.bp.attach(this.c, this.mk);
        this.p = { sustain: 0.5, level: 0.5 };
        this.apply();
    }
    Comp.prototype.set = Boost.prototype.set;
    Comp.prototype.apply = function () {
        setP(this.c.threshold, -12 - this.p.sustain * 26, this.ctx);
        setP(this.mk.gain, expMap(this.p.level, 0.6, 2.2), this.ctx);
    };

    /*
     * Wah: a resonant band-pass swept 350 Hz - 2.3 kHz. Manual (a position)
     * or auto: the strings' envelope (worklet channel 1) is scaled into the
     * filter frequency as an audio-rate modulation, so a harder note opens
     * the wah further - the envelope filter of funk.
     */
    function Wah(ctx) {
        this.ctx = ctx;
        this.bp = new Bypass(ctx);
        this.input = this.bp.input; this.output = this.bp.output;
        this.bpf = biq(ctx, 'bandpass', 700, 4.2);
        this.lp = biq(ctx, 'lowpass', 2600, 0.7);
        this.mixW = ctx.createGain(); this.mixW.gain.value = 3.2;
        this.mixD = ctx.createGain(); this.mixD.gain.value = 0.18;
        this.sum = ctx.createGain();
        this.inG = ctx.createGain();
        this.inG.connect(this.bpf); this.bpf.connect(this.mixW); this.mixW.connect(this.sum);
        this.inG.connect(this.mixD); this.mixD.connect(this.sum);
        this.sum.connect(this.lp);
        this.bp.attach(this.inG, this.lp);
        this.envIn = ctx.createGain();       // connect the envelope here
        this.envIn.gain.value = 0;
        this.envIn.connect(this.bpf.frequency);
        this.p = { mode: 'auto', pos: 0.5, sens: 0.5 };
        this.apply();
    }
    Wah.prototype.set = function (p) {
        for (var k in p) if (k !== 'on') this.p[k] = p[k];
        if (p.on !== undefined) this.bp.set(p.on);
        this.apply();
    };
    Wah.prototype.apply = function () {
        var ctx = this.ctx, p = this.p;
        if (p.mode === 'auto') {
            setP(this.bpf.frequency, 380, ctx);
            setP(this.envIn.gain, 2500 + 9000 * p.sens, ctx);
        } else {
            setP(this.envIn.gain, 0, ctx);
            setP(this.bpf.frequency, expMap(p.pos, 350, 2300), ctx, 0.02);
        }
    };

    // ─────────────────────────────── studio fx ────────────────────────────
    /* Modulation: chorus / flanger / phaser / tremolo / vibe, stereo out. */
    function Mod(ctx) {
        this.ctx = ctx;
        this.input = ctx.createGain();
        this.output = ctx.createChannelMerger(2);
        this.dryL = ctx.createGain(); this.dryR = ctx.createGain();
        this.input.connect(this.dryL); this.input.connect(this.dryR);
        this.dryL.connect(this.output, 0, 0); this.dryR.connect(this.output, 0, 1);
        this.nodes = [];
        this.osc = [];
        this.type = null;
        this.on = false;
        this.p = { rate: 0.4, depth: 0.5, mix: 0.5 };
    }
    Mod.prototype.teardown = function () {
        for (var i = 0; i < this.osc.length; i++) { try { this.osc[i].stop(); } catch (e) {} }
        for (var j = 0; j < this.nodes.length; j++) { try { this.nodes[j].disconnect(); } catch (e) {} }
        this.nodes = []; this.osc = [];
        try { this.input.disconnect(); } catch (e) {}
        this.input.connect(this.dryL); this.input.connect(this.dryR);
        this.dryL.gain.value = 1; this.dryR.gain.value = 1;
        this.n = null;
    };
    Mod.prototype.build = function () {
        var ctx = this.ctx, p = this.p, t = this.type;
        this.teardown();
        if (!this.on || !t) return;
        var self = this;
        function keep(x) { self.nodes.push(x); return x; }
        function lfo(freq, typeW) {
            var o = ctx.createOscillator(); o.type = typeW || 'sine'; o.frequency.value = freq;
            o.start(); self.osc.push(o); self.nodes.push(o); return o;
        }
        var n = {};
        if (t === 'chorus' || t === 'flanger' || t === 'vibe') {
            var base = t === 'flanger' ? 0.0025 : (t === 'vibe' ? 0.004 : 0.014);
            var depth = t === 'flanger' ? 0.0022 : (t === 'vibe' ? 0.0022 : 0.0035);
            var rate = t === 'flanger' ? expMap(p.rate, 0.05, 1.2) : expMap(p.rate, 0.15, 5);
            n.o = lfo(rate);
            n.dL = keep(ctx.createDelay(0.05)); n.dR = keep(ctx.createDelay(0.05));
            n.dL.delayTime.value = base; n.dR.delayTime.value = base;
            n.gL = keep(ctx.createGain()); n.gR = keep(ctx.createGain());
            n.gL.gain.value = depth * p.depth * 2; n.gR.gain.value = -depth * p.depth * 2;
            n.o.connect(n.gL); n.o.connect(n.gR);
            n.gL.connect(n.dL.delayTime); n.gR.connect(n.dR.delayTime);
            n.wL = keep(ctx.createGain()); n.wR = keep(ctx.createGain());
            this.input.connect(n.dL); this.input.connect(n.dR);
            n.dL.connect(n.wL); n.dR.connect(n.wR);
            n.wL.connect(this.output, 0, 0); n.wR.connect(this.output, 0, 1);
            if (t === 'flanger') {
                n.fb = keep(ctx.createGain()); n.fb.gain.value = 0.55;
                n.dL.connect(n.fb); n.fb.connect(n.dL);
                n.fb2 = keep(ctx.createGain()); n.fb2.gain.value = 0.55;
                n.dR.connect(n.fb2); n.fb2.connect(n.dR);
            }
            var wet = t === 'vibe' ? 1 : p.mix;
            n.wL.gain.value = wet; n.wR.gain.value = wet;
            this.dryL.gain.value = t === 'vibe' ? 0 : 1 - p.mix * 0.35;
            this.dryR.gain.value = this.dryL.gain.value;
        } else if (t === 'phaser') {
            n.o = lfo(expMap(p.rate, 0.08, 3));
            n.lfoG = keep(ctx.createGain()); n.lfoG.gain.value = 600 * p.depth + 150;
            n.o.connect(n.lfoG);
            var prev = this.input;
            n.aps = [];
            for (var i = 0; i < 4; i++) {
                var ap = keep(biq(ctx, 'allpass', 700 + i * 220, 0.6));
                n.lfoG.connect(ap.frequency);
                prev.connect(ap); prev = ap; n.aps.push(ap);
            }
            n.fb = keep(ctx.createGain()); n.fb.gain.value = 0.35;
            n.fbd = keep(ctx.createDelay(0.01)); n.fbd.delayTime.value = 0.0005;
            prev.connect(n.fbd); n.fbd.connect(n.fb); n.fb.connect(n.aps[0]);
            n.w = keep(ctx.createGain()); n.w.gain.value = p.mix;
            prev.connect(n.w);
            n.w.connect(this.output, 0, 0); n.w.connect(this.output, 0, 1);
            this.dryL.gain.value = 1; this.dryR.gain.value = 1;
        } else if (t === 'tremolo') {
            n.o = lfo(expMap(p.rate, 2, 10), 'sine');
            n.vca = keep(ctx.createGain());
            n.vca.gain.value = 1 - p.depth * 0.5;
            n.og = keep(ctx.createGain()); n.og.gain.value = p.depth * 0.5;
            n.o.connect(n.og); n.og.connect(n.vca.gain);
            try { this.input.disconnect(); } catch (e) {}
            this.input.connect(n.vca);
            n.vca.connect(this.dryL); n.vca.connect(this.dryR);
            this.dryL.gain.value = 1; this.dryR.gain.value = 1;
        }
        this.n = n;
    };
    /* Rate, depth and mix change the running graph in place, with a short glide.
       They used to rebuild it: a knob drag fired ~60 rebuilds a second - new
       oscillators and delay lines, the LFO phase reset, the dry level jumping to
       1 and back - heard as clicks and stutter. Type and on/off still rebuild. */
    Mod.prototype._update = function () {
        var n = this.n, p = this.p, t = this.type, now = this.ctx.currentTime;
        if (!n || !this.on) return;
        function to(param, v) { if (param) param.setTargetAtTime(v, now, 0.02); }
        if (t === 'chorus' || t === 'flanger' || t === 'vibe') {
            var depth = t === 'flanger' ? 0.0022 : (t === 'vibe' ? 0.0022 : 0.0035);
            to(n.o.frequency, t === 'flanger' ? expMap(p.rate, 0.05, 1.2) : expMap(p.rate, 0.15, 5));
            to(n.gL.gain, depth * p.depth * 2); to(n.gR.gain, -depth * p.depth * 2);
            var wet = t === 'vibe' ? 1 : p.mix;
            to(n.wL.gain, wet); to(n.wR.gain, wet);
            var dry = t === 'vibe' ? 0 : 1 - p.mix * 0.35;
            to(this.dryL.gain, dry); to(this.dryR.gain, dry);
        } else if (t === 'phaser') {
            to(n.o.frequency, expMap(p.rate, 0.08, 3));
            to(n.lfoG.gain, 600 * p.depth + 150);
            to(n.w.gain, p.mix);
        } else if (t === 'tremolo') {
            to(n.o.frequency, expMap(p.rate, 2, 10));
            to(n.vca.gain, 1 - p.depth * 0.5);
            to(n.og.gain, p.depth * 0.5);
        }
    };
    Mod.prototype.set = function (p) {
        var rebuild = false, changed = false;
        if (p.type !== undefined && p.type !== this.type) { this.type = p.type; rebuild = true; }
        if (p.on !== undefined && !!p.on !== this.on) { this.on = !!p.on; rebuild = true; }
        for (var k in p) if (k !== 'type' && k !== 'on') { if (this.p[k] !== p[k]) changed = true; this.p[k] = p[k]; }
        if (rebuild || (changed && !this.n)) this.build();
        else if (changed) this._update();
    };

    /* Delay: digital / analog (darker repeats) / tape (darker + wow), stereo. */
    var DELAY_MAX_FB = 0.85;         // потолок ручки повторов (он же в eg-app.js)
    var LOOP_LP_Q_DB = -3.0103;      // 20·log10(1/√2): Баттерворт в единицах Web Audio (дБ)
    function Delay(ctx) {
        this.ctx = ctx;
        this.bp = new Bypass(ctx);
        this.input = this.bp.input;
        this.output = ctx.createGain();
        this.inG = ctx.createGain();
        this.dL = ctx.createDelay(2.5); this.dR = ctx.createDelay(2.5);
        this.fbL = ctx.createGain(); this.fbR = ctx.createGain();
        /* Оба lowpass стоят В ПЕТЛЕ (L → R → L), а Q у lowpass Web Audio читает в
           децибелах: «0.6» — резонанс +1.67 дБ у среза, усиление петли на этой
           частоте 0.85 × 1.21 = 1.03, и при ручке повторов выше 0.825 эхо не
           гасло, а росло (+0.6 дБ/с на максимуме, замер 07.10.2026). LOOP_LP_Q_DB —
           Баттерворт: усиление фильтра нигде не выше единицы. */
        this.lpL = biq(ctx, 'lowpass', 4000, LOOP_LP_Q_DB); this.lpR = biq(ctx, 'lowpass', 4000, LOOP_LP_Q_DB);
        this.hp = biq(ctx, 'highpass', 120, 0.6);
        this.wet = ctx.createGain();
        this.merge = ctx.createChannelMerger(2);
        this.inG.connect(this.hp);
        this.hp.connect(this.dL);
        // ping-pong-ish: L feeds R feeds L
        this.dL.connect(this.lpL); this.lpL.connect(this.fbL); this.fbL.connect(this.dR);
        this.dR.connect(this.lpR); this.lpR.connect(this.fbR); this.fbR.connect(this.dL);
        this.lpL.connect(this.merge, 0, 0); this.lpR.connect(this.merge, 0, 1);
        this.merge.connect(this.wet);
        this.bp.attach(this.inG, this.wet);
        this.bp.output.connect(this.output);
        this.wow = ctx.createOscillator(); this.wow.frequency.value = 0.6;
        this.wowG = ctx.createGain(); this.wowG.gain.value = 0;
        this.wow.connect(this.wowG); this.wowG.connect(this.dL.delayTime); this.wowG.connect(this.dR.delayTime);
        this.wow.start();
        this.p = { time: 0.38, fb: 0.3, mix: 0.25, type: 'analog' };
        this.apply();
    }
    Delay.prototype.set = function (p) {
        for (var k in p) if (k !== 'on') this.p[k] = p[k];
        if (p.on !== undefined) this.bp.set(p.on, true);
        this.apply();
    };
    Delay.prototype.apply = function () {
        var ctx = this.ctx, p = this.p;
        var t = clamp(p.time, 0.03, 2.2);
        setP(this.dL.delayTime, t, ctx, 0.05);
        setP(this.dR.delayTime, t, ctx, 0.05);
        setP(this.fbL.gain, clamp(p.fb, 0, DELAY_MAX_FB), ctx);
        setP(this.fbR.gain, clamp(p.fb, 0, DELAY_MAX_FB), ctx);
        var lp = p.type === 'digital' ? 12000 : (p.type === 'tape' ? 2600 : 3600);
        setP(this.lpL.frequency, lp, ctx); setP(this.lpR.frequency, lp, ctx);
        setP(this.wowG.gain, p.type === 'tape' ? 0.0012 : 0, ctx);
        setP(this.wet.gain, clamp(p.mix, 0, 1) * 1.2, ctx);
    };

    /*
     * Reverb. Spring is a feedback network (delay + dispersive all-passes =
     * the "drip" of a real tank); the rest are convolution with a generated
     * IR.
     */
    function Reverb(ctx) {
        this.ctx = ctx;
        this.bp = new Bypass(ctx);
        this.input = this.bp.input;
        this.output = ctx.createGain();
        this.bp.output.connect(this.output);
        this.inG = ctx.createGain();
        this.wet = ctx.createGain();
        this.nodes = [];
        this.type = null;
        this.builtDecay = null;
        this.p = { type: 'plate', decay: 0.5, mix: 0.2 };
        this.bp.attach(this.inG, this.wet);
        // built when first switched on (the IR of a hall is 8 s of stereo noise)
    }
    Reverb.prototype.build = function (type) {
        var ctx = this.ctx;
        for (var i = 0; i < this.nodes.length; i++) { try { this.nodes[i].disconnect(); } catch (e) {} }
        this.nodes = [];
        try { this.inG.disconnect(); } catch (e) {}
        this.type = type;
        var self = this;
        function keep(x) { self.nodes.push(x); return x; }
        if (type === 'spring') {
            var hp = keep(biq(ctx, 'highpass', 250, 0.7));
            var d1 = keep(ctx.createDelay(0.2)); d1.delayTime.value = 0.0337;
            var d2 = keep(ctx.createDelay(0.2)); d2.delayTime.value = 0.0413;
            var fb1 = keep(ctx.createGain()), fb2 = keep(ctx.createGain());
            var lp = keep(biq(ctx, 'lowpass', 4200, 0.7));
            this.inG.connect(hp);
            var springs = [[d1, fb1, 1200], [d2, fb2, 1700]];
            var merge = keep(ctx.createChannelMerger(2));
            for (var s = 0; s < 2; s++) {
                var dl = springs[s][0], fb = springs[s][1];
                var prev = dl;
                hp.connect(dl);
                for (var a = 0; a < 6; a++) {
                    var ap = keep(biq(ctx, 'allpass', springs[s][2] + a * 90, 0.5));
                    prev.connect(ap); prev = ap;
                }
                prev.connect(fb); fb.connect(dl);
                prev.connect(merge, 0, s);
                this.nodes.push(fb);
            }
            merge.connect(lp);
            lp.connect(this.wet);
            this.fbs = [fb1, fb2];
        } else {
            var conv = keep(ctx.createConvolver());
            conv.normalize = false;
            var dec = this.p.decay;
            assignConv(conv, reverbBuffer(ctx, type, dec), function () { return reverbBuffer(ctx, type, dec, true); });
            this.builtDecay = this.p.decay;
            var pre = keep(biq(ctx, 'highpass', 180, 0.7));
            var cg = keep(ctx.createGain());
            this.inG.connect(pre); pre.connect(conv); conv.connect(cg); cg.connect(this.wet);
            this.conv = conv; this.convG = cg; this.convPre = pre;
            this.fbs = null;
        }
        this.apply();
    };
    /* A new decay swaps in a new convolver beside the old one and crossfades
       (~80 ms); a knob drag waits for 180 ms of stillness first. It used to
       rebuild the whole reverb on every 0.05 step of the knob - an abrupt cut of
       the tail each time and a fresh impulse response (up to 3.4 MB) per step. */
    Reverb.prototype._redecay = function () {
        var self = this;
        if (isOffline(this.ctx)) { this._swapConv(); return; }
        clearTimeout(this._dt);
        this._dt = setTimeout(function () {
            self._dt = null;
            if (self.bp.on && Math.abs(self.p.decay - self.builtDecay) > 0.05) self._swapConv();
        }, 180);
    };
    Reverb.prototype._swapConv = function () {
        var ctx = this.ctx, now = ctx.currentTime, oldC = this.conv, oldG = this.convG;
        if (!oldC || !this.convPre) { this.build(this.p.type); return; }
        var conv = ctx.createConvolver(), type = this.type, dec = this.p.decay;
        conv.normalize = false;
        assignConv(conv, reverbBuffer(ctx, type, dec), function () { return reverbBuffer(ctx, type, dec, true); });
        this.builtDecay = this.p.decay;
        var g = ctx.createGain(); g.gain.value = 0;
        this.convPre.connect(conv); conv.connect(g); g.connect(this.wet);
        g.gain.setTargetAtTime(1, now, 0.025);
        oldG.gain.setTargetAtTime(0, now, 0.025);
        this.nodes.push(conv, g);
        this.conv = conv; this.convG = g;
        var self = this;
        setTimeout(function () {
            try { oldC.disconnect(); oldG.disconnect(); } catch (e) {}
            self.nodes = self.nodes.filter(function (x) { return x !== oldC && x !== oldG; });
        }, 400);
    };
    Reverb.prototype.set = function (p) {
        for (var k in p) if (k !== 'on' && (k !== 'type' || p.type)) this.p[k] = p[k];
        var on = p.on !== undefined ? !!p.on : this.bp.on;
        var typeStale = this.type !== this.p.type || (!this.conv && !this.fbs);
        var decayStale = !typeStale && this.p.type !== 'spring' && Math.abs(this.p.decay - this.builtDecay) > 0.05;
        if (on && typeStale) this.build(this.p.type);
        else if (on && decayStale) this._redecay();
        if (p.on !== undefined) this.bp.set(p.on, true);
        this.apply();
    };
    Reverb.prototype.apply = function () {
        var ctx = this.ctx, p = this.p;
        var mix = clamp(p.mix, 0, 1);
        if (this.fbs) {
            setP(this.fbs[0].gain, 0.62 + p.decay * 0.28, ctx);
            setP(this.fbs[1].gain, 0.6 + p.decay * 0.28, ctx);
            setP(this.wet.gain, mix * 0.9, ctx);
        } else {
            setP(this.wet.gain, mix * 1.6, ctx);
        }
    };

    // ──────────────────────────────── rig ─────────────────────────────────
    /*
     * The last stage of every output (rig, band, export): a fast compressor
     * for the body of the peak, then a soft ceiling for what gets through.
     * The Web Audio compressor adds its own make-up gain (spec: the 0.6 power
     * of the curve's gain at 0 dBFS, +1.1 dB at -2 dB / 20:1), so on its own
     * it let clean transients reach +1.3 dBFS (surf, funk presets) - hard
     * clipping on the speakers and in the WAV. The make-up is cancelled and
     * the ceiling is identity to -1.4 dBFS, never above -0.2 dBFS.
     */
    function compressorMakeup(thresholdDb, ratio) {
        var full = thresholdDb + (0 - thresholdDb) / ratio;
        return Math.pow(Math.pow(10, -full / 20), 0.6);
    }
    var CEIL_CURVE = null;
    function ceilingCurve() {
        if (CEIL_CURVE) return CEIL_CURVE;
        // spans -2..2 (a 0.5 gain sits in front), so peaks up to +6 dBFS land
        // on the smooth part instead of the curve's clamped ends
        var n = 4096, c = new Float32Array(n), a = 0.85, top = 0.975;
        for (var i = 0; i < n; i++) {
            var x = (i / (n - 1)) * 4 - 2, m = Math.abs(x);
            var y = m <= a ? m : a + (top - a) * Math.tanh((m - a) / (top - a));
            c[i] = x < 0 ? -y : y;
        }
        return (CEIL_CURVE = c);
    }
    function Safety(ctx, thresholdDb) {
        var t = thresholdDb === undefined ? -2 : thresholdDb;
        this.comp = ctx.createDynamicsCompressor();
        this.comp.threshold.value = t; this.comp.knee.value = 0; this.comp.ratio.value = 20;
        this.comp.attack.value = 0.002; this.comp.release.value = 0.12;
        this.cut = ctx.createGain(); this.cut.gain.value = 0.5 / compressorMakeup(t, 20);
        this.shaper = ctx.createWaveShaper();
        this.shaper.curve = ceilingCurve();
        /* NOT oversampled: the down-sampling filter after an oversampled
           shaper rings over the curve's top. Measured (OfflineAudioContext,
           clipped noise at +5 dB): '2x' 1.027 peak and 110 samples above the
           ceiling per 2 s, '4x' 1.026, 'none' 0.905 - and a distorted take
           exported with 1255 samples at full scale. The curve is identity to
           0.85 and a gentle knee above it, so its aliasing is negligible. */
        this.shaper.oversample = 'none';
        this.comp.connect(this.cut); this.cut.connect(this.shaper);
        this.input = this.comp; this.output = this.shaper;
    }
    /* `opts.state` = the state the rig starts in (same schema as apply()):
       the amp and cabinet are then built once, with it, instead of as
       defaults that the first apply() replaces. */
    function Rig(ctx, opts) {
        opts = opts || {};
        var st0 = opts.state || {};
        this.ctx = ctx;
        this.input = ctx.createGain();       // guitar DI in
        this.envIn = ctx.createGain();       // DI envelope in (auto-wah)
        this.output = ctx.createGain();
        this.inputTrim = ctx.createGain();
        this.pickup = biq(ctx, 'lowpass', 4200, 2.2);
        this.guitarVol = ctx.createGain();
        this.post = ctx.createGain();        // a REAL guitar joins here: it has its own pickup

        this.comp = new Comp(ctx);
        this.wah = new Wah(ctx);
        this.boost = new Boost(ctx);
        this.drive = new Drive(ctx);
        this.amp = new Amp(ctx, (st0.amp && st0.amp.model) || 'plexi', st0.amp);
        this.cab = ctx.createConvolver();
        this.cab.normalize = false;
        this.cabOn = true;
        this.cabDry = ctx.createGain(); this.cabDry.gain.value = 0;
        this.cabWet = ctx.createGain(); this.cabWet.gain.value = 1;
        this.cabMix = ctx.createGain();
        this.mic = biq(ctx, 'highshelf', 3000, 0.7, 0);
        this.mod = new Mod(ctx);
        this.delay = new Delay(ctx);
        this.reverb = new Reverb(ctx);
        this.trim = ctx.createGain();
        this.master = ctx.createGain();
        this.safety = new Safety(ctx, -2);

        chain([this.input, this.inputTrim, this.pickup, this.guitarVol, this.post, this.comp.input]);
        this.comp.output.connect(this.wah.input);
        this.envIn.connect(this.wah.envIn);
        this.wah.output.connect(this.boost.input);
        this.boost.output.connect(this.drive.input);
        this.drive.output.connect(this.amp.input);
        this.amp.output.connect(this.cab);
        this.amp.output.connect(this.cabDry);
        this.cab.connect(this.cabWet);
        this.cabWet.connect(this.cabMix); this.cabDry.connect(this.cabMix);
        chain([this.cabMix, this.mic, this.mod.input]);
        this.mod.output.connect(this.delay.input);
        this.delay.output.connect(this.reverb.input);
        chain([this.reverb.output, this.trim, this.master, this.safety.input]);
        this.safety.output.connect(this.output);

        this.state = null;
        this.setCab((st0.cab && st0.cab.type) || 'v30');
        if (opts.state) this.apply(opts.state);
    }
    Rig.prototype.setCab = function (key) {
        if (key === 'off') {
            this.cabOn = false;
            setP(this.cabWet.gain, 0, this.ctx); setP(this.cabDry.gain, 0.12, this.ctx);
            return;
        }
        var ctx = this.ctx, buf = cabBuffer(ctx, key);
        // each assignment builds a new kernel: only a different buffer is assigned
        if (this.cab.buffer !== buf && !assignConv(this.cab, buf, function () { return cabBuffer(ctx, key, true); })) {
            // the amp without a cabinet is a harsh sound, but a sound: the 'off' path
            this.cabError = 'refused';
            this.cabOn = false;
            setP(this.cabWet.gain, 0, ctx); setP(this.cabDry.gain, 0.12, ctx);
            return;
        }
        this.cabOn = true;
        setP(this.cabWet.gain, 1, this.ctx); setP(this.cabDry.gain, 0, this.ctx);
    };
    /*
     * Apply a whole preset (see eg-presets.js for the schema). Only the parts
     * present are touched, so the UI can pass a partial object for one knob.
     */
    Rig.prototype.apply = function (st) {
        var ctx = this.ctx;
        if (!st) return;
        if (st.guitar) {
            var g = st.guitar;
            if (g.res) {
                var toneK = g.tone === undefined ? 1 : g.tone;
                // tone pot: resonance falls and flattens as the knob closes
                setP(this.pickup.frequency, g.res.f * (0.28 + 0.72 * Math.pow(toneK, 1.6)), ctx, 0.02);
                setP(this.pickup.Q, 0.7 + (g.res.q - 0.7) * toneK, ctx, 0.02);
            }
            if (g.out !== undefined || g.vol !== undefined) {
                var v = g.vol === undefined ? 1 : g.vol;
                setP(this.guitarVol.gain, (g.out || 1) * v * v, ctx);
            }
        }
        var pd = st.pedals || {};
        if (pd.comp) this.comp.set(pd.comp);
        if (pd.wah) this.wah.set(pd.wah);
        if (pd.boost) this.boost.set(pd.boost);
        if (pd.drive) this.drive.set(pd.drive);
        if (st.amp) {
            if (st.amp.model && st.amp.model !== this.amp.model) {
                // the new amp is built with the new knobs, not the old amp's
                for (var ak in st.amp) if (Object.prototype.hasOwnProperty.call(st.amp, ak) && ak !== 'model') this.amp.p[ak] = st.amp[ak];
                this.amp.build(st.amp.model);
            }
            this.amp.set(st.amp);
        }
        if (st.cab) {
            if (st.cab.type) this.setCab(st.cab.type);
            if (st.cab.mic !== undefined) setP(this.mic.gain, (0.5 - st.cab.mic) * 10, ctx);
        }
        if (st.mod) this.mod.set(st.mod);
        if (st.delay) this.delay.set(st.delay);
        if (st.reverb) this.reverb.set(st.reverb);
        if (st.trim !== undefined) setP(this.trim.gain, db(st.trim), ctx);
        if (st.master !== undefined) setP(this.master.gain, clamp(st.master, 0, 1.5), ctx);
    };
    Rig.prototype.dispose = function () {
        try { this.output.disconnect(); } catch (e) {}
        this.mod.teardown();
        try { this.delay.wow.stop(); } catch (e) {}
    };

    return {
        VERSION: 1,
        Rig: Rig,
        Delay: Delay,            // on its own for tests/js/echo-loops-decay.test.mjs
        AMPS: AMPS,
        CABS: CABS,
        STACKS: STACKS,
        stackResponseDb: stackResponseDb,
        fmvPoly: fmvPoly,
        bilinear3: bilinear3,
        curve: curve,
        cabBuffer: cabBuffer,
        registerCab: registerCab,
        CAB_KEYS: ['v30', 'greenback', 'dv77', 'g12f', '2x12o', '1x12o'],
        reverbBuffer: reverbBuffer,
        minPhaseIR: minPhaseIR,
        Safety: Safety,
        analyser: analyser,
        readLevel: readLevel,
        ceilingCurve: ceilingCurve,
        compressorMakeup: compressorMakeup
    };
}));
